import { createInstance, dateReviver } from '@/api';
import {
    CodeListAttributeMappingClient,
    CodeListAttributeMappingDto,
    SaveCodeListAttributeMappingDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useEntityModelEditStore } from '../entity-model/entity-model-edit.store';

export interface CodeListAttributeMappingDetailStoreState {
    detail?: CodeListAttributeMappingDto;
    loading: boolean;
    reloading: boolean;
}

export const client = new CodeListAttributeMappingClient('', createInstance(), dateReviver);

export const useCodeListAttributeMappingDetailStore = defineStore(
    'configuration-code-list-attribute-mapping-detail-store',
    {
        state: (): CodeListAttributeMappingDetailStoreState => ({
            detail: null,
            loading: false,
            reloading: false
        }),
        getters: {},
        actions: {
            async get(id: number, reloading: boolean = false) {
                this.detail = null;
                reloading ? (this.reloading = true) : (this.loading = true);
                try {
                    this.detail = await client.getCodeListAttributeMapping(id);
                    return this.detail;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('codeListAttributeMapping.detail.error.get.title'),
                        error,
                        false
                    );
                } finally {
                    reloading ? (this.reloading = false) : (this.loading = false);
                }
            },
            async save(id: number, dto: SaveCodeListAttributeMappingDto) {
                try {
                    this.reloading = true;
                    await client.save(id, dto);
                    useAlertStore().setSuccess(i18n.global.t('codeListAttributeMapping.detail.success.save.title'));
                    await this.get(id);
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('codeListAttributeMapping.detail.error.save.title'),
                        error,
                        false
                    );
                } finally {
                    this.reloading = false;
                    useEntityModelEditStore().closeEdit();
                }
            },
            async remove(id: number): Promise<boolean> {
                try {
                    this.reloading = true;
                    await client.save(id, {});
                    useAlertStore().setSuccess(i18n.global.t('codeListAttributeMapping.detail.success.delete.title'));
                    this.detail = null;
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('codeListAttributeMapping.detail.error.delete.title'),
                        error,
                        false
                    );
                } finally {
                    this.reloading = false;
                    useEntityModelEditStore().closeEdit();
                }
                return false;
            },
            clear() {
                this.detail = null;
            }
        }
    }
);
